import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { MotionConfig } from 'motion/react';
import { getNovaSuggestions, dismissNovaSuggestion, saveNovaPreferences } from '@/api';
import { NovaPreferences, NovaSuggestion } from '@/types';
import { useNovaRefreshedUser } from '@/hooks/useNovaRefreshedUser';
import { SystemFeaturesContext, UserContext } from '../ContextProviders';
import NovaAvatar from './NovaAvatar';
import NovaPanel from './NovaPanel';
import NovaSuggestionToast from './NovaSuggestionToast';
import { NovaThemeName } from './novaTheme';

const NOVA_THEME_STORAGE_KEY = 'nova-theme';
const NOVA_PANEL_OPEN_STORAGE_KEY = 'nova-panel-open';

const DEFAULT_PREFERENCES: NovaPreferences = {
  proactiveSuggestionsEnabled: true,
  dismissedSuggestionIds: [],
  documentUploadConsent: false,
};

interface NovaContextType {
  isOpen: boolean;
  openNova: (opts?: { seedQuery?: string }) => void;
  closeNova: () => void;
  toggleNova: () => void;
  seedQuery: string | null;
  activeConversationId: string | null;
  setActiveConversationId: (id: string | null) => void;
  suggestions: NovaSuggestion[];
  dismissSuggestion: (suggestionId: string) => void;
  preferences: NovaPreferences;
  updatePreferences: (partial: Partial<NovaPreferences>) => void;
  theme: NovaThemeName;
  toggleTheme: () => void;
}

const NovaContext = createContext<NovaContextType | null>(null);

export const useNova = () => {
  const context = useContext(NovaContext);
  if (!context) {
    throw new Error('useNova must be used within a NovaProvider');
  }
  return context;
};

interface NovaProviderProps {
  children: React.ReactNode;
  /** Skip the floating launcher + proactive suggestion toast — used on the standalone Nova page, which is already the Nova experience, so there's nothing for them to launch into. */
  standalone?: boolean;
}

export const NovaProvider: React.FC<NovaProviderProps> = ({ children, standalone }) => {
  // The page's user, or — if it came back anonymous after an idle token expiry —
  // the one re-read after a token refresh. Re-provided below so every Nova
  // component (and anything else inside this provider) sees the real user.
  const user = useNovaRefreshedUser();
  const { aiChatPage } = useContext(SystemFeaturesContext);

  const [isOpen, setIsOpen] = useState(false);
  const [seedQuery, setSeedQuery] = useState<string | null>(null);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<NovaSuggestion[]>([]);
  const [preferences, setPreferences] = useState<NovaPreferences>(DEFAULT_PREFERENCES);
  const [theme, setTheme] = useState<NovaThemeName>('light');

  const isEligible = !!aiChatPage && !!(user.isAuthenticated || user.isIpBasedIdentity);

  const openNova = useCallback((opts?: { seedQuery?: string }) => {
    setSeedQuery(opts?.seedQuery ?? null);
    setIsOpen(true);
  }, []);

  const closeNova = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggleNova = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);


  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(NOVA_THEME_STORAGE_KEY);
      if (saved === 'light' || saved === 'dark') setTheme(saved);
    } catch {
      // Storage can be blocked (private window, site data off) — stay on the default.
    }
  }, []);

  // Reopen the panel if it was open before a navigation or refresh. Only for the
  // floating panel — the standalone Nova pages aren't driven by it. Starts closed
  // for the same hydration reason as the theme above.
  const openStateHydratedRef = useRef(false);
  useEffect(() => {
    if (standalone) return;
    try {
      if (window.sessionStorage.getItem(NOVA_PANEL_OPEN_STORAGE_KEY) === 'true') setIsOpen(true);
    } catch {
      // Storage blocked — the panel just starts closed.
    }
  }, [standalone]);

  useEffect(() => {
    if (standalone) return;
    // Skip the initial render's `false`, which would overwrite the saved value
    // before the effect above has applied it.
    if (!openStateHydratedRef.current) {
      openStateHydratedRef.current = true;
      return;
    }
    try {
      window.sessionStorage.setItem(NOVA_PANEL_OPEN_STORAGE_KEY, String(isOpen));
    } catch {
      // Not persisted, but the panel still opens and closes normally.
    }
  }, [isOpen, standalone]);

  const toggleTheme = useCallback(() => {
    setTheme((prev) => {
      const next: NovaThemeName = prev === 'light' ? 'dark' : 'light';
      try {
        window.localStorage.setItem(NOVA_THEME_STORAGE_KEY, next);
      } catch {
        // Not persisted, but the toggle still works for this page view.
      }
      return next;
    });
  }, []);

  const dismissSuggestion = useCallback(
    (suggestionId: string) => {
      setSuggestions((prev) => prev.filter((s) => s.suggestionId !== suggestionId));
      dismissNovaSuggestion(user?.userId || '', suggestionId);
    },
    [user?.userId],
  );

  const updatePreferences = useCallback(
    (partial: Partial<NovaPreferences>) => {
      setPreferences((prev) => {
        const next = { ...prev, ...partial };
        saveNovaPreferences(user?.userId || '', next);
        return next;
      });
    },
    [user?.userId],
  );

  // HubSpot's Web Interactives script sets an inline min-height: 100% on body to
  // reserve space for its banners, which leaves a stray gap on Nova's own
  // full-page routes once the banner is gone. A class + !important (see
  // globals.css) rather than setting body.style directly, since that script can
  // re-apply its inline style after this effect runs and would otherwise win.
  // Scoped to `standalone` (the dedicated Nova pages) rather than all of
  // globals.css, since NovaProvider also mounts on every other page just to
  // render the floating launcher widget.
  useEffect(() => {
    if (!standalone) return;
    document.body.classList.add('nova-standalone-page');
    return () => {
      document.body.classList.remove('nova-standalone-page');
    };
  }, [standalone]);

  useEffect(() => {
    if (!isEligible || !preferences.proactiveSuggestionsEnabled) {
      setSuggestions([]);
      return;
    }
    if (!user?.userId) return;
    getNovaSuggestions(user.userId)
      .then((response) => {
        if (response.succeeded) setSuggestions(response.suggestions);
      })
      .catch(() => { });
  }, [isEligible, preferences.proactiveSuggestionsEnabled, user?.userId]);

  const contextValue = useMemo(
    () => ({
      isOpen,
      openNova,
      closeNova,
      toggleNova,
      seedQuery,
      activeConversationId,
      setActiveConversationId,
      suggestions,
      dismissSuggestion,
      preferences,
      updatePreferences,
      theme,
      toggleTheme,
    }),
    [
      isOpen,
      openNova,
      closeNova,
      toggleNova,
      seedQuery,
      activeConversationId,
      suggestions,
      dismissSuggestion,
      preferences,
      updatePreferences,
      theme,
      toggleTheme,
    ],
  );

  return (
    <UserContext value={user}>
      <NovaContext.Provider value={contextValue}>
        {children}
        {isEligible && (
          // Context only — components read prefersReducedMotion via useReducedMotion()
          // themselves (NovaMark, NovaPanel, NovaThinkingIndicator) since framer-motion's
          // MotionConfig doesn't auto-strip transform values from declarative animate props.
          <MotionConfig reducedMotion="user">
            {!standalone && <NovaAvatar />}
            <NovaPanel persistSession={!standalone} />
            {!standalone && <NovaSuggestionToast />}
          </MotionConfig>
        )}
      </NovaContext.Provider>
    </UserContext>
  );
};
